import { useLocation, useNavigate } from 'react-router';

import {
  ClipboardPen,
  FileCheck2,
  FileSearch,
  FileText,
  Users,
} from 'lucide-react';

import { DEV_USERS, getUserInfo, setDevUser } from '@/lib/auth.js';

import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Select } from './ui/select';
import { Separator } from './ui/separator';

const AUTH_ENABLED = import.meta.env.VITE_ENABLE_AUTH === 'true';

const navList = [
  {
    match: ['/documents', '/document/'],
    text: 'Documents',
    to: '/documents',
  },
  {
    icon: FileSearch,
    match: ['/controls', '/control/'],
    text: 'Change Control',
    to: '/controls',
  },
  {
    icon: FileCheck2,
    match: ['/qualities', '/quality/'],
    text: 'Quality Event',
    to: '/qualities',
  },
  {
    icon: ClipboardPen,
    match: ['/audit-trail'],
    text: 'Audit Trail',
    to: '/audit-trail',
  },
];

const NavButton = ({
  icon: Icon = FileText,
  match,
  mobile,
  setMobileMenuOpen,
  sidebarCollapsed,
  text,
  to,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isMatch = match?.filter((item) => pathname.startsWith(item))?.length;

  const handleClick = () => {
    navigate(to);
    mobile && setMobileMenuOpen(false);
  };

  return (
    <Button
      className={`${
        sidebarCollapsed && !mobile ? 'w-10 h-10 p-0' : 'w-full justify-start'
      }`}
      onClick={handleClick}
      variant={isMatch ? 'default' : 'ghost'}
    >
      <Icon className="h-4 w-4" />
      {(!sidebarCollapsed || mobile) && <span className="ml-1">{text}</span>}
    </Button>
  );
};

const SidebarContent = ({
  mobile = false,
  sidebarCollapsed,
  setMobileMenuOpen,
}) => {
  const userInfo = getUserInfo();
  const displayName =
    userInfo?.name ||
    userInfo?.email ||
    userInfo?.['cognito:username'] ||
    'User';
  const initials = displayName.slice(0, 2).toUpperCase();
  const groups = userInfo?.['cognito:groups'] || [];
  const roleLabel = groups.length > 0 ? groups.join(', ') : '';

  return (
    <div className="space-y-4 py-4">
      <div className="px-3 py-2">
        {(!sidebarCollapsed || mobile) && (
          <div className="space-y-1">
            {/* User Info */}
            <div className="flex items-center space-x-3 px-2 py-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{displayName}</span>
                {roleLabel && (
                  <span className="text-xs text-muted-foreground">
                    {roleLabel}
                  </span>
                )}
              </div>
            </div>
            <Separator />
            {!AUTH_ENABLED && (
              <div className="px-2 pt-1 pb-0">
                <p className="text-xs text-muted-foreground mb-1">
                  Switch User role
                </p>
                <Select
                  className="h-9 text-sm"
                  value={userInfo?.sub}
                  onChange={(e) => {
                    setDevUser(e.target.value);
                    window.location.reload();
                  }}
                >
                  {DEV_USERS.map((u) => (
                    <option key={u.sub} value={u.sub}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}

        {sidebarCollapsed && !mobile && (
          <div className="flex justify-center py-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 px-3 py-2">
        {navList.map(({ icon, match, text, to }) => (
          <NavButton
            key={to}
            {...{
              match,
              mobile,
              setMobileMenuOpen,
              sidebarCollapsed,
              text,
              to,
              icon,
            }}
          />
        ))}
        {(groups.includes('PowerUser') || groups.includes('SuperAdmin')) && (
          <NavButton
            mobile={mobile}
            setMobileMenuOpen={setMobileMenuOpen}
            sidebarCollapsed={sidebarCollapsed}
            text="User Management"
            to="/admin/users"
            icon={Users}
          />
        )}
      </div>
    </div>
  );
};

export default SidebarContent;
